import {
  Credenza,
  CredenzaContent,
  CredenzaHeader,
  CredenzaTrigger,
  CredenzaTitle,
  CredenzaDescription,
  CredenzaBody,
  CredenzaFooter,
  CredenzaClose,
} from '@/components/ui/credenza.tsx';
import { CopyIcon, LoaderIcon, PlusIcon } from 'lucide-react';
import { Button } from '@/components/ui/button.tsx';
import { useState } from 'react';
import {
  CreateApiKeyResponse,
  useCreateApiKeyMutation,
} from '@/features/user-profile/hooks/mutations/use-create-api-key.ts';
import { Label } from '@/components/ui/label.tsx';
import { Input } from '@/components/ui/input.tsx';
import { cn } from '@/utils/utils.ts';
import { ActionTooltip } from '@/components/ui/action-tooltip.tsx';

type ApiKeyModalState = {
  currentStep: 'input' | 'display';
  createdKey: {
    partialHint: string;
    secretKey: string;
  } | null;
};

export const CreateApiKeyDialogButton = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalState, setModalState] = useState<ApiKeyModalState>({
    currentStep: 'input',
    createdKey: null,
  });

  const handleApiKeyCreated = (response: CreateApiKeyResponse) => {
    setModalState({
      currentStep: 'display',
      createdKey: {
        partialHint: response.partialHint,
        secretKey: response.key,
      },
    });
  };

  const resetModal = () => {
    setModalState({
      currentStep: 'input',
      createdKey: null,
    });
  };

  return (
    <Credenza
      open={isModalOpen}
      onOpenChange={(open) => {
        setIsModalOpen(open);
        if (!open) resetModal();
      }}
    >
      <CredenzaTrigger asChild>
        <Button className="flex gap-2">
          <PlusIcon />
          Create key
        </Button>
      </CredenzaTrigger>
      <CredenzaContent>
        {modalState.currentStep === 'input' && (
          <ApiKeyCreationForm onApiKeyCreated={handleApiKeyCreated} />
        )}
        {modalState.currentStep === 'display' && modalState.createdKey && (
          <ApiKeyDisplay
            partialHint={modalState.createdKey.partialHint}
            secretKey={modalState.createdKey.secretKey}
          />
        )}
      </CredenzaContent>
    </Credenza>
  );
};

function ApiKeyCreationForm({
  onApiKeyCreated,
}: {
  onApiKeyCreated: (response: CreateApiKeyResponse) => void;
}) {
  const [keyName, setKeyName] = useState('');
  const { mutate: createApiKey, isPending: isCreatingKey } = useCreateApiKeyMutation({
    onSuccess: onApiKeyCreated,
  });

  const handleCreateKey = () => {
    if (keyName.trim()) {
      createApiKey({ name: keyName.trim() });
    }
  };

  return (
    <>
      <CredenzaHeader>
        <CredenzaTitle>Create API Key</CredenzaTitle>
        <CredenzaDescription>
          Create a new API key to authenticate your requests.
        </CredenzaDescription>
      </CredenzaHeader>
      <CredenzaBody>
        <div className="mt-2 flex flex-col gap-2">
          <Label htmlFor="key-name">Key Name</Label>
          <Input
            id="key-name"
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            placeholder="my-api-key"
            disabled={isCreatingKey}
          />
        </div>
      </CredenzaBody>
      <CredenzaFooter>
        <Button
          onClick={handleCreateKey}
          disabled={isCreatingKey || !keyName.trim()}
          className={cn('transition-all duration-200')}
        >
          {isCreatingKey && (
            <LoaderIcon className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
          )}
          {isCreatingKey ? 'Creating...' : 'Create Key'}
        </Button>
      </CredenzaFooter>
    </>
  );
}

interface ApiKeyDisplayProps {
  partialHint?: string;
  secretKey: string;
}

function ApiKeyDisplay({ secretKey }: ApiKeyDisplayProps) {
  const handleCopyKey = async () => {
    await navigator.clipboard.writeText(secretKey || '');
  };

  return (
    <>
      <CredenzaHeader>
        <CredenzaTitle>Save Your API Key</CredenzaTitle>
        <CredenzaDescription>
          Please save your secret key in a safe place since you won't be able to view it again.
          Keep it secure, as anyone with your API key can make requests on your behalf. If you
          lose it, you'll need to generate a new one.
        </CredenzaDescription>
      </CredenzaHeader>
      <CredenzaBody>
        <div className="flex min-h-[150px] w-full flex-col justify-between rounded-lg bg-gray-50 p-2">
          <div className="h-full flex-1 p-2">
            <p className="font-mono text-sm break-all">{secretKey}</p>
          </div>
          <ActionTooltip
            asChild
            align="end"
            title="Copy API key"
            clickedTitle="API key copied"
            onClick={handleCopyKey}
          >
            <Button
              variant="secondary"
              className="flex w-min items-center gap-2 self-end rounded-md border border-transparent bg-white p-2 hover:border-gray-300 hover:bg-white"
            >
              <CopyIcon className="size-4 text-gray-600" />
              Copy
            </Button>
          </ActionTooltip>
        </div>
      </CredenzaBody>
      <CredenzaFooter>
        <CredenzaClose asChild>
          <Button variant="secondary">Close</Button>
        </CredenzaClose>
      </CredenzaFooter>
    </>
  );
}
